import React from "react";
import { Tooltip } from 'react-tooltip'
import { NavigateFunction } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Sorter from "../components/Sorter";
import Filter from "../components/Filter";
import Pager from "../components/Pager";
import TrackerLine from "../components/TrackerLine";
import FilterLine from "../components/FilterLine";

import EndPointService from "../services/endpoint";
import SorterService from "../services/sorter";
import FilterService from "../services/filter";
import PagerService from "../services/pager";

import {Tracker} from "../types";

import './Trackers.scss'
import withRouter from "../hooks/withRouter";

// **********************************************************************************************************

type TrackerDisplay = Tracker & {
    last_formated: string;
}

type TrackersPageProps = {
    navigate: NavigateFunction;
};

type BulkSelection = {
    [key: number]: boolean,
}

type TrackersPageState = {
    trackers: Tracker[];
    toDisplay: TrackerDisplay[];
    servers: string[];
    selected: BulkSelection;
    ports: number[];
    serverConnectionAlert: boolean,
};

// **********************************************************************************************************

function onlyUnique<T>(value: T, index: number, array: T[]): boolean {
    return array.indexOf(value) === index;
}

function formatTimestamp(ts: number): string {
    const date = new Date(ts);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    return ''
        + String(year).padStart(4, '0')
        + '/'
        + String(month).padStart(2, '0')
        + '/'
        + String(day).padStart(2, '0')
        + ' '
        + String(hours).padStart(2, '0')
        + ':'
        + String(minutes).padStart(2, '0')
        + ':'
        + String(seconds).padStart(2, '0');
}

// **********************************************************************************************************

const REFRESH_SECONDS = 10;

// **********************************************************************************************************

class TrackersPage extends React.Component<TrackersPageProps, TrackersPageState> {
    static timeoutReloadTrackers: any = null;

    constructor(props: TrackersPageProps) {
        super(props);
        this.state = {
            trackers: [],
            toDisplay: [],
            selected: {},
            servers: [FilterService.SERVER_URL_FILTER_ALL_VALUE],
            ports: [FilterService.SERVER_PORT_FILTER_ALL_VALUE],
            serverConnectionAlert: false,
        };
    }

    componentDidMount() {
        this.updateTrackersList();
    }

    componentWillUnmount() {
        clearTimeout(TrackersPage.timeoutReloadTrackers);
    }

    calculateFiltered(trackers: Tracker[]): Tracker[] {
        return trackers
            .filter((tracker, index) => FilterService.filter(tracker, index))
    }

    calculateDisplay(filtered: Tracker[]): TrackerDisplay[] {
        return filtered
            .sort((t1, t2) => SorterService.sort(t1, t2))
            .filter((tracker, index) => PagerService.isCurrentPage(tracker, index))
            .map(tracker => ({...tracker, last_formated: formatTimestamp(tracker.last)}));
    }

    updateTrackersList(): void {
        clearTimeout(TrackersPage.timeoutReloadTrackers);
        EndPointService.trackers()
            .then(list => {
                const servers = list
                    .map(tracker => tracker.server_url)
                    .filter(onlyUnique)
                    .sort((s1, s2) => s1 < s2 ? -1 : 1)
                const ports = list
                    .map(tracker => tracker.server_port)
                    .filter(onlyUnique)
                    .sort((s1, s2) => s1 < s2 ? -1 : 1)
                const filtered: Tracker[] = this.calculateFiltered(list)

                PagerService.setLength(list.length, filtered.length);

                this.setState({
                    trackers: list,
                    toDisplay: this.calculateDisplay(filtered),
                    servers: [
                        FilterService.SERVER_URL_FILTER_ALL_VALUE,
                        ...servers,
                    ],
                    ports: [
                        FilterService.SERVER_PORT_FILTER_ALL_VALUE,
                        ...ports,
                    ],
                    serverConnectionAlert: false,
                })
            })
            .catch(() => {
                if (!this.state.serverConnectionAlert) {
                    alert('Une erreur est survenue lors de la récupération des trackers.')
                    this.setState({
                        serverConnectionAlert: true,
                    })
                }
            })
            .finally(() => {
                TrackersPage.timeoutReloadTrackers = setTimeout(() => {
                    this.updateTrackersList();
                }, REFRESH_SECONDS * 1000);
            })
    }

    edit(id: number) {
        const { navigate } = this.props;
        navigate("/tracker/"+id, { replace: false });
    }

    bulkEdit(selected: BulkSelection) {
        const list = Object.keys(selected)
            .map(key => Number(key))
            .filter(id => selected[id]);
        const { navigate } = this.props;
        if (list.length === 0) {
            alert('Selectionnez les trackers à rediriger.');
        } else {
            navigate("/trackers/redirect?ids=" + list.join(','), { replace: false });
        }
    }

    remove(id: number) {
        this.setState({
            trackers: this.state.trackers.map(t => {
                if (t.id === id) {
                    t.removed = true;
                }
                return t
            })
        });
    }

    updateFilter() {
        const filtered: Tracker[] = this.calculateFiltered(this.state.trackers)
        PagerService.setLength(this.state.trackers.length, filtered.length);
        this.setState({
            toDisplay: this.calculateDisplay(filtered),
        })
    }

    setTrackerSelection(trackerId: number, selected: boolean) {
        this.setState({
            selected: {
                ...this.state.selected,
                [trackerId]: selected,
            },
        });
    }

    render() {
        return (
            <div className="trackers-page">
                <h1 className="page-title">Liste des trackers</h1>
                <Tooltip id="info-filter" />
                <table className="trackers">
                    <thead className="trackers-headers">
                        <tr className="trackers-header">
                            <td className="tracker-bulk-action pointer">
                                <div onClick={() => this.bulkEdit(this.state.selected)}>
                                    <FontAwesomeIcon icon="forward"/>
                                </div>
                            </td>
                            <th className="tracker-name">
                                Nom
                                <Sorter code='sort_name' onChange={() => this.updateFilter()} />
                            </th>
                            <th className="tracker-hardware_id">
                                Hardware ID
                                <Sorter code='sort_hardwareId' onChange={() => this.updateFilter()} />
                            </th>
                            <th className="tracker-battery">
                                <FontAwesomeIcon icon="battery-half" />
                                <Sorter code='sort_battery' onChange={() => this.updateFilter()} />
                            </th>
                            <th className="tracker-version">
                                <FontAwesomeIcon icon="microchip" />
                                <Sorter code='sort_version' onChange={() => this.updateFilter()} />
                            </th>
                            <th className="tracker-date">
                                Dernière connexion
                                <Sorter code='sort_last' onChange={() => this.updateFilter()} />
                            </th>
                            {/* <th className="tracker-updatable"></th> */}
                            <th className="tracker-url-port" colSpan={2}>
                                Serveur
                                <Sorter code='sort_server' onChange={() => this.updateFilter()} />
                            </th>
                            <th className="tracker-pager" colSpan={2}>
                                <Pager onChange={() => this.updateFilter()}/>
                            </th>
                            <th className="tracker-filter pointer" style={{color: FilterService.isOpen() ? 'green' : 'white'}}>
                                <Filter type='switch' code='filter_open' icon="filter" onChange={() => { this.updateFilter()}} />
                            </th>
                        </tr>
                        <FilterLine
                            servers={this.state.servers}
                            ports={this.state.ports}
                            onChange={() => this.updateFilter()} />
                    </thead>
                    <tbody className="trackers-list">
                        { this.state.toDisplay
                            .map(tracker =>
                                <TrackerLine
                                    key={tracker.id}
                                    id={tracker.id}
                                    name={tracker.name}
                                    hardware_id={tracker.hardware_id}
                                    battery={tracker.battery}
                                    version={tracker.version}
                                    last={tracker.last_formated}
                                    updatable={tracker.updatable}
                                    server_url={tracker.server_url}
                                    server_port={tracker.server_port}
                                    fleetstate={tracker.fleetstate}
                                    removed={tracker.removed}
                                    selected={this.state.selected[tracker.id]}
                                    onEdit={() => this.edit(tracker.id)}
                                    onRemove={() => this.remove(tracker.id)}
                                    onSelect={selected => this.setTrackerSelection(tracker.id, selected)}
                                    />
                            )
                        }
                    </tbody>
                </table>
            </div>
        );
    }
}

export default withRouter(TrackersPage);